import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import ScopeTree from "@/components/ScopeTree";
import AssignmentProgressDialog from "@/components/AssignmentProgressDialog";
import AccountSecurityDialog from "@/components/AccountSecurityDialog";
import { Banner, Button, ConfirmDialog, Panel, PasswordInput, Spinner } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createUser,
  deleteUser,
  ApiError,
  errorDetail,
  listProjects,
  listUsers,
  listZones,
  listWards,
  resetUserPassword,
  getUserScopes,
  setUserScopes,
} from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import type { AuthUser, Project, ScopeGrant, Zone, Ward } from "@/lib/types";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/format";
import {
  Search,
  UserCheck,
  UserPlus,
  KeyRound,
  MapPin,
  Trash2,
  Filter,
  BarChart3,
  ShieldCheck,
} from "lucide-react";
import { useLanguage } from "@/i18n";
import type { Translations } from "@/i18n/en";

/**
 * Account management for field surveyors and office staff.
 *
 * This page used to be create-only, and the right-hand panel could show nothing
 * but the accounts created since the last page load -- because the backend had
 * no endpoint that listed users. A reload lost the list, so there was no way to
 * see who existed, remove someone who had left, or help a surveyor who had
 * forgotten their password (these accounts carry no email, so there is no
 * self-service reset). The workaround was deleting and recreating the account.
 *
 * GET /auth/users, DELETE /auth/users/{id} and POST /auth/users/{id}/password
 * now exist, so the panel is a real directory.
 *
 * The role reference below is documentation, not a source of truth: every rule
 * it describes is enforced in the backend (app/services/block_access.py and the
 * require_staff dependency on each route). It is here because the Role picker
 * offers three options whose actual consequences were written down nowhere in
 * the product -- the picker's one-line hint was the only guidance, and it
 * covered only the admin/staff distinction. If a rule changes server-side, this
 * table has to be updated by hand.
 */

/** What each role can do, as enforced by the backend. */
const ROLE_MATRIX: {
  capability: string;
  surveyor: string;
  staff: string;
  admin: string;
}[] = [
  {
    capability: "See blocks",
    surveyor: "Pending + in-progress",
    staff: "All, incl. submitted",
    admin: "All, incl. submitted",
  },
  {
    capability: "See submitted survey data",
    surveyor: "No",
    staff: "Yes",
    admin: "Yes",
  },
  {
    capability: "Claim a block",
    surveyor: "Unclaimed only",
    staff: "Opens claimed ones read-only",
    admin: "Opens claimed ones read-only",
  },
  {
    capability: "Collect / edit shapes",
    surveyor: "In blocks they can see",
    staff: "Yes",
    admin: "Yes",
  },
  {
    capability: "Submit a block",
    surveyor: "Own claimed block",
    staff: "Any block",
    admin: "Any block",
  },
  {
    capability: "Edit a submitted block's data",
    surveyor: "No",
    staff: "Yes, in place",
    admin: "Yes, in place",
  },
  {
    capability: "Reopen (send back for rework)",
    surveyor: "No",
    staff: "Yes",
    admin: "Yes",
  },
  {
    capability: "Export shapefile",
    surveyor: "Single block only",
    staff: "Whole project",
    admin: "Whole project",
  },
  {
    capability: "Projects / zones / wards / blocks",
    surveyor: "Read only",
    staff: "Create + delete",
    admin: "Create + delete",
  },
  {
    capability: "Drone imagery + property outlines",
    surveyor: "No access",
    staff: "Upload, register, delete",
    admin: "Upload, register, delete",
  },
  {
    capability: "Which projects",
    surveyor: "Only those assigned",
    staff: "Only those assigned",
    admin: "All, always",
  },
  {
    capability: "Manage accounts",
    surveyor: "No",
    staff: "Surveyors + staff",
    admin: "Anyone, incl. other admins",
  },
  {
    capability: "Which projects",
    surveyor: "Only those assigned",
    staff: "Only those assigned",
    admin: "All, always",
  },
  {
    capability: "Assign projects to accounts",
    surveyor: "No",
    staff: "Yes",
    admin: "Yes",
  },
];
/**
 * Whether an account works in this project. Null means "any project".
 *
 * Three cases, and conflating any two of them gets the row list wrong:
 *
 * 1. An UNRESTRICTED account (project_ids and scopes both null -- an admin)
 *    matches every project. It can reach all of them, so filtering to one
 *    should not make it disappear.
 * 2. A PROJECT-level grant matches directly.
 * 3. A grant made further DOWN -- a zone, ward or single block -- still means
 *    this account works in that project. Those grants carry no project id of
 *    their own, which is why the server sends `project_id` alongside each one
 *    (see ScopeGrant); matching on `level === "project"` alone would hide
 *    every surveyor assigned to a ward, which is most of them.
 */
function matchesProject(user: AuthUser, projectId: number | null): boolean {
  if (projectId === null) return true;
  if (user.scopes == null && user.project_ids == null) return true;
  if ((user.project_ids ?? []).includes(projectId)) return true;
  return (user.scopes ?? []).some(
    (g) => g.project_id === projectId || (g.level === "project" && g.ref_id === projectId)
  );
}

export default function UsersPage({
  defaultTab = "manage",
}: {
  defaultTab?: "manage" | "create";
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Active view: either "manage" or "create" based on URL or prop
  const activeTab = location.pathname === "/users/create" ? "create" : defaultTab;

  // Form state
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"surveyor" | "staff" | "admin">("surveyor");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search & filter state for Managing view
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  /** Project id as a string, or "all". Kept as a string because that is what
   *  Radix Select hands back, and converting once at the point of comparison
   *  beats converting on every render. */
  const [projectFilter, setProjectFilter] = useState<string>("all");

  const [projects, setProjects] = useState<Project[] | null>(null);
  const [newWardIds, setNewWardIds] = useState<number[]>([]);

  const [users, setUsers] = useState<AuthUser[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  // Modal states
  const [confirmDelete, setConfirmDelete] = useState<AuthUser | null>(null);
  const [resetFor, setResetFor] = useState<AuthUser | null>(null);
  const [progressFor, setProgressFor] = useState<AuthUser | null>(null);
  const [securityFor, setSecurityFor] = useState<AuthUser | null>(null);
  const [assigningUser, setAssigningUser] = useState<AuthUser | null>(null);
  const [assignScopes, setAssignScopes] = useState<ScopeGrant[] | null>(null);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const me = getStoredUser();

  useEffect(() => {
    if (me?.role === "surveyor") {
      navigate("/", { replace: true });
    }
  }, [me, navigate]);

  const refresh = useCallback(async () => {
    if (getStoredUser()?.role === "surveyor") return;
    try {
      setUsers(await listUsers());
      setListError(null);
    } catch (err) {
      setListError(errorDetail(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (getStoredUser()?.role === "surveyor") return;
    listProjects()
      .then(setProjects)
      .catch(() => setProjects([]));
  }, []);

  // Filtered users based on search, role and project
  const filteredUsers = useMemo(() => {
    if (!users) return null;
    const wantedProject = projectFilter === "all" ? null : Number(projectFilter);
    return users.filter((u) => {
      const matchesRole = roleFilter === "all" || u.role === roleFilter;
      const q = searchQuery.trim().toLowerCase();
      const matchesQuery =
        !q ||
        u.username.toLowerCase().includes(q) ||
        (u.full_name ?? "").toLowerCase().includes(q);
      return matchesRole && matchesQuery && matchesProject(u, wantedProject);
    });
  }, [users, roleFilter, projectFilter, searchQuery]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    if (role !== "admin" && newWardIds.length === 0) {
      setError("Select at least one ward before creating this account.");
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const created = await createUser({
        username: username.trim(),
        password,
        full_name: fullName.trim() || undefined,
        role,
        ward_ids: role === "admin" ? [] : newWardIds,
      });
      setUsername("");
      setFullName("");
      setPassword("");
      setNewWardIds([]);
      setNotice(
        role === "admin"
          ? `Created ${created.username} — an administrator with access to all projects.`
          : `Created ${created.username} with ${newWardIds.length} ward assignment(s).`
      );
      await refresh();
      navigate("/users");
    } catch (err) {
      const text = errorDetail(err);
      setError(
        text.includes("already")
          ? t.usersPage.usernameAlreadyTaken
          : err instanceof ApiError && err.status === 403
            ? t.usersPage.onlyStaffCanCreate
            : text
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeUser(user: AuthUser) {
    setBusyId(user.id);
    setListError(null);
    setNotice(null);
    try {
      await deleteUser(user.id);
      setConfirmDelete(null);
      setNotice(t.usersPage.deletedAccountNotice(user.username));
      await refresh();
    } catch (err) {
      setListError(errorDetail(err));
    } finally {
      setBusyId(null);
    }
  }

  async function changePassword(user: AuthUser, next: string) {
    setBusyId(user.id);
    setListError(null);
    setNotice(null);
    try {
      await resetUserPassword(user.id, next);
      setResetFor(null);
      setNotice(t.usersPage.passwordUpdatedNotice(user.username));
    } catch (err) {
      setListError(errorDetail(err));
    } finally {
      setBusyId(null);
    }
  }

  async function saveAssignments(user: AuthUser, scopes: ScopeGrant[]) {
    if (user.role !== "admin") {
      const hasWardOrBlock = scopes.some((g) => g.level === "ward" || g.level === "block");
      if (!hasWardOrBlock) {
        setListError("At least one ward or block assignment is required for this account.");
        return;
      }
    }
    setBusyId(user.id);
    setListError(null);
    setNotice(null);
    try {
      await setUserScopes(user.id, scopes);
      setAssigningUser(null);
      setAssignScopes(null);
      setNotice(
        scopes.length === 0
          ? t.usersPage.noAssignmentsNotice(user.username)
          : t.usersPage.updatedAssignmentsNotice(user.username, scopes.length)
      );
      await refresh();
    } catch (err) {
      setListError(errorDetail(err));
    } finally {
      setBusyId(null);
    }
  }

  function openAssignmentModal(u: AuthUser) {
    setAssigningUser(u);
    setAssignScopes(null);
    getUserScopes(u.id)
      .then(setAssignScopes)
      .catch(() => setAssignScopes([]));
  }

  const weakPassword = password.length > 0 && password.length < 8;

  return (
    <AppShell
      crumbs={[
        { label: t.usersPage.crumbAccounts, href: "/users" },
        { label: activeTab === "create" ? t.usersPage.crumbCreation : t.usersPage.crumbManaging },
      ]}
    >
      <div className="mx-auto max-w-[1300px] p-5">
        {/* Header and View Tabs */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-ink">
              {activeTab === "create" ? t.usersPage.headingCreate : t.usersPage.headingManage}
            </h1>
            <p className="mt-0.5 text-xs text-ink-muted">
              {activeTab === "create"
                ? t.usersPage.subheadingCreate
                : t.usersPage.subheadingManage}
            </p>
          </div>

          {/* Subpage navigation tabs */}
          <div className="flex items-center rounded-lg bg-surface-sunken p-1 border border-line shadow-sm">
            <Link
              to="/users"
              className={cn(
                "flex items-center gap-2 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all",
                activeTab === "manage"
                  ? "bg-surface shadow-ring text-ink font-semibold"
                  : "text-ink-muted hover:text-ink"
              )}
            >
              <UserCheck className="h-4 w-4 text-accent-solid" />
              <span>{t.usersPage.tabManaging}</span>
              {users && (
                <span className="rounded-full bg-surface-sunken px-1.5 py-0.2 text-2xs text-ink-secondary">
                  {users.length}
                </span>
              )}
            </Link>

            <Link
              to="/users/create"
              className={cn(
                "flex items-center gap-2 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all",
                activeTab === "create"
                  ? "bg-surface shadow-ring text-ink font-semibold"
                  : "text-ink-muted hover:text-ink"
              )}
            >
              <UserPlus className="h-4 w-4 text-accent-solid" />
              <span>{t.usersPage.tabCreation}</span>
            </Link>
          </div>
        </div>

        {/* Global Notices */}
        {notice && (
          <div className="mb-4">
            <Banner tone="info">{notice}</Banner>
          </div>
        )}
        {listError && (
          <div className="mb-4">
            <Banner tone="danger">{listError}</Banner>
          </div>
        )}

        {/* =========================================================================
            VIEW 1: MANAGING (Account list + Search + Type Filter + Assignment Modal)
           ========================================================================= */}
        {activeTab === "manage" && (
          <div className="space-y-4">
            {/* Search and Filters Bar */}
            <div className="flex flex-col gap-3 rounded-lg bg-surface p-3.5 border border-line shadow-ring sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                {/* Search in Accounts */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t.usersPage.searchPlaceholder}
                    className="h-9 w-full rounded-md border border-line bg-surface-sunken pl-9 pr-3 text-xs text-ink placeholder:text-ink-muted focus:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-accent-ring transition"
                  />
                </div>

                {/* Account Type (Role) Filter placed beside search */}
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-muted shrink-0">
                    <Filter className="h-3.5 w-3.5" />
                    {t.usersPage.roleLabel}
                  </span>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="h-9 w-36 text-xs bg-surface-sunken border-line">
                      <SelectValue placeholder={t.usersPage.allRoles} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.usersPage.allRoles}</SelectItem>
                      <SelectItem value="surveyor">{t.usersPage.roleSurveyor}</SelectItem>
                      <SelectItem value="staff">{t.usersPage.roleStaff}</SelectItem>
                      <SelectItem value="admin">{t.usersPage.roleAdmin}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Project filter. Only offered once the project list has
                    arrived and holds something -- a lone "All projects" is a
                    control that cannot do anything. */}
                {projects && projects.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-muted shrink-0">
                      <MapPin className="h-3.5 w-3.5" />
                      {t.usersPage.projectLabel}
                    </span>
                    <Select value={projectFilter} onValueChange={setProjectFilter}>
                      <SelectTrigger className="h-9 w-44 text-xs bg-surface-sunken border-line">
                        <SelectValue placeholder={t.usersPage.allProjects} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.usersPage.allProjects}</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Quick action to create */}
              <Link to="/users/create">
                <Button size="sm" variant="primary" className="gap-1.5 whitespace-nowrap">
                  <UserPlus className="h-3.5 w-3.5" />
                  {t.usersPage.newAccount}
                </Button>
              </Link>
            </div>

            {/* Accounts Directory Table / List */}
            <Panel
              title={
                filteredUsers
                  ? `Accounts (${filteredUsers.length}${users && filteredUsers.length !== users.length ? ` of ${users.length}` : ""})`
                  : t.usersPage.accountsTitlePlain
              }
            >
              {filteredUsers === null ? (
                <div className="flex items-center justify-center gap-2 p-12 text-sm text-ink-muted">
                  <Spinner className="h-4 w-4" /> {t.usersPage.loadingAccounts}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-12 text-center text-xs text-ink-muted">
                  {users?.length === 0
                    ? t.usersPage.noAccountsYet
                    : t.usersPage.noAccountsMatch}
                </div>
              ) : (
                <div className="divide-y divide-line">
                  {filteredUsers.map((u) => {
                    const isMe = me?.id === u.id;
                    const busy = busyId === u.id;

                    const roleBadgeClass =
                      u.role === "admin"
                        ? "bg-accent-subtle text-accent-deep border border-accent font-semibold"
                        : u.role === "staff"
                          ? "bg-amber-50 text-amber-800 border border-amber-200 font-medium"
                          : "bg-surface-sunken text-ink-secondary border border-line font-medium";

                    const hasWardGrant =
                      u.role === "admin" ||
                      u.scopes == null ||
                      u.scopes.some((g) => g.level === "ward" || g.level === "block");

                    return (
                      <div
                        key={u.id}
                        className="flex flex-col gap-3 p-4 transition hover:bg-surface-sunken/40 sm:flex-row sm:items-center sm:justify-between"
                      >
                        {/* Account Info */}
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-ink">
                              {u.full_name || u.username}
                            </span>
                            {isMe && (
                              <span className="rounded bg-accent-subtle px-1.5 py-0.5 text-2xs font-bold text-accent-solid border border-accent/60">
                                {t.usersPage.you}
                              </span>
                            )}
                            <span
                              className={cn(
                                "rounded px-2 py-0.5 text-2xs font-medium capitalize",
                                roleBadgeClass
                              )}
                            >
                              {u.role}
                            </span>
                            {!hasWardGrant && (
                              <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-2xs font-medium text-amber-700">
                                No ward assigned
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-3 text-2xs text-ink-muted">
                            <span>
                              {t.usersPage.usernameLabel}{" "}
                              <code className="text-ink font-mono">{u.username}</code>
                            </span>
                            <span>•</span>
                            <ProjectSummary user={u} projects={projects} />
                          </div>

                          <SecurityBadges user={u} />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Change Password */}
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() => setResetFor(u)}
                            className="text-2xs gap-1"
                          >
                            <KeyRound className="h-3 w-3 text-ink-muted" />
                            {t.usersPage.password}
                          </Button>

                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() => setSecurityFor(u)}
                            className="text-2xs gap-1"
                          >
                            <ShieldCheck className="h-3 w-3 text-accent" />
                            {t.accountSecurity.button}
                          </Button>

                          {/* Progress. Offered for every non-admin account,
                              including one assigned nothing: the dialog says
                              so, which is more use than a missing button. */}
                          {u.role !== "admin" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => setProgressFor(u)}
                              className="text-2xs gap-1"
                            >
                              <BarChart3 className="h-3 w-3 text-accent" />
                              {t.usersPage.progress}
                            </Button>
                          )}

                          {/* Assignment popup trigger (not for admins who have all-access) */}
                          {u.role !== "admin" ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => openAssignmentModal(u)}
                              className="text-2xs gap-1"
                            >
                              <MapPin className="h-3 w-3 text-accent" />
                              {t.usersPage.assignment}
                            </Button>
                          ) : (
                            <span className="text-2xs text-ink-muted px-2 py-1">
                              {t.usersPage.allAccessAdmin}
                            </span>
                          )}

                          {/* Delete Account */}
                          {!isMe && (
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={busy}
                              onClick={() => setConfirmDelete(u)}
                              className="text-2xs gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              {t.usersPage.delete}
                            </Button>
                          )}

                          {busy && <Spinner className="h-3.5 w-3.5" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>

            {/* ASSIGNMENT POPUP DIALOG */}
            <Dialog
              open={assigningUser !== null}
              onOpenChange={(open) => {
                if (!open && busyId !== assigningUser?.id) {
                  setAssigningUser(null);
                  setAssignScopes(null);
                }
              }}
            >
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-accent" />
                    <span>{t.usersPage.assignSurveyAreasTitle}</span>
                  </DialogTitle>
                  <DialogDescription>
                    {t.usersPage.assignSurveyAreasDescriptionPrefix}{" "}
                    <span className="font-semibold text-ink">
                      {assigningUser?.full_name
                        ? `${assigningUser.full_name} (${assigningUser.username})`
                        : assigningUser?.username}
                    </span>
                    .
                  </DialogDescription>
                </DialogHeader>

                {assignScopes === null ? (
                  <div className="flex items-center justify-center gap-2 py-12 text-xs text-ink-muted">
                    <Spinner className="h-4 w-4" /> {t.usersPage.loadingGrants}
                  </div>
                ) : (
                  <ScopeTree
                    disabled={busyId === assigningUser?.id}
                    initial={assignScopes}
                    onCancel={() => {
                      setAssigningUser(null);
                      setAssignScopes(null);
                    }}
                    onSubmit={(scopes) =>
                      assigningUser && void saveAssignments(assigningUser, scopes)
                    }
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* ASSIGNMENT PROGRESS POPUP DIALOG */}
            <AssignmentProgressDialog
              user={progressFor}
              onOpenChange={(open) => {
                if (!open) setProgressFor(null);
              }}
            />

            {/* PHONE & SESSION DIALOG */}
            <AccountSecurityDialog
              user={securityFor}
              onOpenChange={(open) => {
                if (!open) setSecurityFor(null);
              }}
              onChanged={(text) => {
                setNotice(text);
                void refresh();
              }}
            />

            {/* PASSWORD RESET POPUP DIALOG */}
            <Dialog
              open={resetFor !== null}
              onOpenChange={(open) => {
                if (!open && busyId !== resetFor?.id) setResetFor(null);
              }}
            >
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-accent-solid" />
                    <span>{t.usersPage.changePasswordTitle}</span>
                  </DialogTitle>
                  <DialogDescription>
                    {t.usersPage.changePasswordDescriptionPrefix}{" "}
                    <span className="font-semibold text-ink">{resetFor?.username}</span>.
                  </DialogDescription>
                </DialogHeader>
                {resetFor && (
                  <PasswordReset
                    disabled={busyId === resetFor.id}
                    onCancel={() => setResetFor(null)}
                    onSubmit={(next) => void changePassword(resetFor, next)}
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* CONFIRM DELETE DIALOG */}
            <ConfirmDialog
              open={confirmDelete !== null}
              title={t.usersPage.deleteAccountTitle}
              body={
                <>
                  {t.usersPage.deleteAccountBodyPrefix}{" "}
                  <span className="font-medium text-ink">{confirmDelete?.username}</span>
                  {t.usersPage.deleteAccountBodySuffix}
                </>
              }
              confirmLabel={t.usersPage.deleteAccountConfirmLabel}
              onCancel={() => setConfirmDelete(null)}
              onConfirm={() => confirmDelete && void removeUser(confirmDelete)}
              busy={busyId === confirmDelete?.id}
            />
          </div>
        )}

        {/* =========================================================================
            VIEW 2: CREATION (New Account Form + "What each role can do" Matrix)
           ========================================================================= */}
        {activeTab === "create" && (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            {/* New Account Form (5 cols) */}
            <div className="lg:col-span-5">
              <Panel title={t.usersPage.newAccountPanelTitle}>
                <form onSubmit={submit} className="space-y-4 p-4">
                  <Field label={t.usersPage.fieldUsernameLabel} hint={t.usersPage.fieldUsernameHint}>
                    <input
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="off"
                      placeholder="e.g. jdoe"
                      className="h-9 w-full rounded border border-line bg-surface-sunken px-2.5 text-sm text-ink shadow-ring focus:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-accent-ring"
                    />
                  </Field>

                  <Field label={t.usersPage.fieldFullNameLabel} hint={t.usersPage.fieldFullNameHint}>
                    <input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="h-9 w-full rounded border border-line bg-surface-sunken px-2.5 text-sm text-ink shadow-ring focus:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-accent-ring"
                    />
                  </Field>

                  <Field
                    label={t.usersPage.fieldPasswordLabel}
                    hint={t.usersPage.fieldPasswordHint}
                  >
                    <PasswordInput
                      value={password}
                      onChange={setPassword}
                      autoComplete="new-password"
                      placeholder={t.usersPage.passwordPlaceholder}
                    />
                    {weakPassword && (
                      <p className="mt-1 text-2xs text-status-text-critical">
                        {t.usersPage.weakPassword}
                      </p>
                    )}
                  </Field>

                  <Field
                    label={t.usersPage.fieldRoleLabel}
                    hint={t.usersPage.fieldRoleHint}
                  >
                    <div className="flex gap-1.5">
                      {(["surveyor", "staff", "admin"] as const)
                        .filter((r) => r !== "admin" || me?.role === "admin")
                        .map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setRole(r)}
                            aria-pressed={role === r}
                            className={cn(
                              "h-9 flex-1 rounded text-xs font-medium capitalize transition shadow-ring",
                              role === r
                                ? "bg-accent-solid text-white font-semibold shadow-sm"
                                : "bg-surface-sunken text-ink-secondary hover:text-ink hover:bg-surface-raised"
                            )}
                          >
                            {r}
                          </button>
                        ))}
                    </div>
                  </Field>

                  {/* Ward assignment (required for non-admin) */}
                  {role !== "admin" && (
                    <Field
                      label={t.usersPage.fieldInitialProjectsLabel}
                      hint={t.usersPage.fieldInitialProjectsHint}
                    >
                      <WardPicker
                        projects={projects}
                        selected={newWardIds}
                        onChange={setNewWardIds}
                        disabled={saving}
                      />
                    </Field>
                  )}

                  {error && <Banner tone="danger">{error}</Banner>}

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    disabled={saving || !username.trim() || password.length < 8 || (role !== "admin" && newWardIds.length === 0)}
                  >
                    {saving ? <Spinner className="h-3.5 w-3.5" /> : null}
                    {saving ? t.usersPage.creatingAccount : t.usersPage.createAccount}
                  </Button>
                </form>
              </Panel>
            </div>

            {/* "What each role can do" Matrix (7 cols) */}
            <div className="lg:col-span-7">
              <Panel title={t.usersPage.roleMatrixTitle}>
                <div className="p-4 space-y-4">
                  <p className="text-xs text-ink-secondary leading-relaxed">
                    {t.usersPage.roleMatrixIntro}
                  </p>

                  {/* Matrix Table */}
                  <div className="thin-scroll overflow-x-auto rounded border border-line">
                    <table className="w-full min-w-[520px] border-collapse text-left text-xs">
                      <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                        <tr>
                          <th className="px-3 py-2 font-semibold">{t.usersPage.colCapability}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t.usersPage.colSurveyor}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t.usersPage.colStaff}</th>
                          <th className="px-3 py-2 font-semibold text-center">{t.usersPage.colAdmin}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {ROLE_MATRIX.map((row) => (
                          <tr key={row.capability} className="hover:bg-surface-sunken/40">
                            <td className="px-3 py-2 font-medium text-ink">
                              {row.capability}
                            </td>
                            <td className="px-3 py-2 text-center text-ink-secondary">
                              {row.surveyor}
                            </td>
                            <td className="px-3 py-2 text-center text-ink-secondary">
                              {row.staff}
                            </td>
                            <td className="px-3 py-2 text-center font-medium text-ink">
                              {row.admin}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Key Operational Guidelines */}
                  <div className="space-y-2 rounded-lg bg-surface-sunken p-3 text-2xs text-ink-muted">
                    <p className="font-semibold text-ink text-xs mb-1">{t.usersPage.rolePrinciplesTitle}</p>
                    <p>
                      <strong className="text-ink-secondary">{t.usersPage.rolePrincipleStaffAdminLabel}</strong>{" "}
                      {t.usersPage.rolePrincipleStaffAdmin}
                    </p>
                    <p>
                      <strong className="text-ink-secondary">{t.usersPage.rolePrincipleFieldLabel}</strong>{" "}
                      {t.usersPage.rolePrincipleField}
                    </p>
                    <p>
                      <strong className="text-ink-secondary">{t.usersPage.rolePrincipleFailClosedLabel}</strong>{" "}
                      {t.usersPage.rolePrincipleFailClosed}
                    </p>
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/**
 * Inline "set a new password" row.
 *
 * Local state, not lifted: the value is a credential, so it should live for as
 * short a time as possible and disappear entirely when the row closes rather
 * than lingering in the page's state after use.
 */
/** Lazy-loading ward picker: Projects → Zones → Wards tree with checkboxes. */
function WardPicker({
  projects,
  selected,
  onChange,
  disabled,
}: {
  projects: Project[] | null;
  selected: number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [expandedZones, setExpandedZones] = useState<Set<number>>(new Set());
  const [zones, setZones] = useState<Record<number, Zone[]>>({});
  const [wards, setWards] = useState<Record<number, Ward[]>>({});
  const [loadingProjects, setLoadingProjects] = useState<Set<number>>(new Set());
  const [loadingZones, setLoadingZones] = useState<Set<number>>(new Set());

  function toggleWard(wardId: number) {
    onChange(
      selected.includes(wardId) ? selected.filter((x) => x !== wardId) : [...selected, wardId]
    );
  }

  function toggleAllZoneWards(zoneId: number, zoneWards: Ward[]) {
    const ids = zoneWards.map((w) => w.id);
    const allOn = ids.every((id) => selected.includes(id));
    if (allOn) {
      onChange(selected.filter((id) => !ids.includes(id)));
    } else {
      const add = ids.filter((id) => !selected.includes(id));
      onChange([...selected, ...add]);
    }
  }

  async function toggleProject(projectId: number) {
    if (expandedProjects.has(projectId)) {
      setExpandedProjects((s) => { const n = new Set(s); n.delete(projectId); return n; });
      return;
    }
    setExpandedProjects((s) => new Set(s).add(projectId));
    if (!zones[projectId]) {
      setLoadingProjects((s) => new Set(s).add(projectId));
      try {
        const zs = await listZones(projectId);
        setZones((z) => ({ ...z, [projectId]: zs }));
      } finally {
        setLoadingProjects((s) => { const n = new Set(s); n.delete(projectId); return n; });
      }
    }
  }

  async function toggleZone(zoneId: number) {
    if (expandedZones.has(zoneId)) {
      setExpandedZones((s) => { const n = new Set(s); n.delete(zoneId); return n; });
      return;
    }
    setExpandedZones((s) => new Set(s).add(zoneId));
    if (!wards[zoneId]) {
      setLoadingZones((s) => new Set(s).add(zoneId));
      try {
        const ws = await listWards(zoneId);
        setWards((w) => ({ ...w, [zoneId]: ws }));
      } finally {
        setLoadingZones((s) => { const n = new Set(s); n.delete(zoneId); return n; });
      }
    }
  }

  if (projects === null) {
    return (
      <div className="flex items-center gap-2 rounded bg-surface-sunken px-2.5 py-2 text-2xs text-ink-muted shadow-ring">
        <Spinner className="h-3 w-3" /> {t.usersPage.loadingProjects}
      </div>
    );
  }
  if (projects.length === 0) {
    return (
      <p className="rounded bg-surface-sunken px-2.5 py-2 text-2xs text-ink-muted shadow-ring">
        {t.usersPage.noProjectsYetCreateOne}
      </p>
    );
  }

  return (
    <div className="rounded bg-surface-sunken shadow-ring">
      <div className="flex items-center justify-between border-b border-line px-2.5 py-1.5">
        <span className="text-2xs text-ink-muted">
          {selected.length} ward{selected.length === 1 ? "" : "s"} selected
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange([])}
            className="text-2xs text-accent-solid hover:underline disabled:opacity-50"
          >
            Clear all
          </button>
        )}
      </div>
      <ul className="thin-scroll max-h-56 overflow-y-auto p-1">
        {projects.map((p) => {
          const isOpen = expandedProjects.has(p.id);
          const loading = loadingProjects.has(p.id);
          const projectZones = zones[p.id] ?? [];
          return (
            <li key={p.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => void toggleProject(p.id)}
                className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left transition hover:bg-surface-raised disabled:opacity-50"
              >
                <span className="text-2xs text-ink-muted w-3">
                  {loading ? "…" : isOpen ? "▾" : "▸"}
                </span>
                <span className="truncate text-xs font-medium text-ink">{p.name}</span>
              </button>
              {isOpen && (
                <ul className="ml-4">
                  {loading && (
                    <li className="flex items-center gap-1.5 px-2 py-1 text-2xs text-ink-muted">
                      <Spinner className="h-2.5 w-2.5" /> Loading zones…
                    </li>
                  )}
                  {projectZones.map((z) => {
                    const zOpen = expandedZones.has(z.id);
                    const zLoading = loadingZones.has(z.id);
                    const zoneWards = wards[z.id] ?? [];
                    const allZoneOn = zoneWards.length > 0 && zoneWards.every((w) => selected.includes(w.id));
                    return (
                      <li key={z.id}>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => void toggleZone(z.id)}
                            className="flex flex-1 items-center gap-1.5 rounded px-1.5 py-1 text-left transition hover:bg-surface-raised disabled:opacity-50"
                          >
                            <span className="text-2xs text-ink-muted w-3">
                              {zLoading ? "…" : zOpen ? "▾" : "▸"}
                            </span>
                            <span className="truncate text-xs text-ink-secondary">{z.name}</span>
                          </button>
                          {zOpen && zoneWards.length > 0 && (
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => toggleAllZoneWards(z.id, zoneWards)}
                              className="shrink-0 pr-1.5 text-2xs text-accent-solid hover:underline disabled:opacity-50"
                            >
                              {allZoneOn ? "Clear" : "All"}
                            </button>
                          )}
                        </div>
                        {zOpen && (
                          <ul className="ml-4">
                            {zLoading && (
                              <li className="flex items-center gap-1.5 px-2 py-1 text-2xs text-ink-muted">
                                <Spinner className="h-2.5 w-2.5" /> Loading wards…
                              </li>
                            )}
                            {zoneWards.map((w) => {
                              const on = selected.includes(w.id);
                              return (
                                <li key={w.id}>
                                  <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => toggleWard(w.id)}
                                    aria-pressed={on}
                                    className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition hover:bg-surface-raised disabled:opacity-50"
                                  >
                                    <span
                                      aria-hidden
                                      className={
                                        "flex h-3.5 w-3.5 flex-none items-center justify-center rounded-sm text-[9px] font-bold " +
                                        (on ? "bg-accent-solid text-white" : "bg-surface-raised shadow-ring")
                                      }
                                    >
                                      {on ? "✓" : ""}
                                    </span>
                                    <span className={"truncate text-xs " + (on ? "text-ink" : "text-ink-secondary")}>
                                      {w.name}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** One-line "what is this account assigned to" readout for an account row. */
function ProjectSummary({
  user,
  projects,
}: {
  user: AuthUser;
  projects: Project[] | null;
}) {
  const { t } = useLanguage();
  // null means unrestricted, [] means nothing -- never collapse the two.
  if (user.scopes == null && user.project_ids == null) {
    return (
      <p className="text-2xs text-ink-muted">
        {t.usersPage.everythingAdmin} <span className="text-ink-muted/70">{t.usersPage.adminSuffix}</span>
      </p>
    );
  }

  const scopes = user.scopes ?? [];
  if (scopes.length === 0) {
    return (
      <p className="text-2xs text-status-text-critical">
        {t.usersPage.nothingAssigned}
      </p>
    );
  }

  // Project grants are named; the narrower levels are counted. Naming a ward
  // would mean fetching every ward of every project just to render a row, and
  // the count is what the reader actually needs -- the tree shows which.
  const projectIds = scopes.filter((g) => g.level === "project").map((g) => g.ref_id);
  const parts: string[] = [];
  if (projectIds.length) {
    const names = projects
      ? projectIds.map((id) => projects.find((p) => p.id === id)?.name ?? `#${id}`)
      : projectIds.map((id) => `#${id}`);
    parts.push(names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : ""));
  }
  for (const [level, noun] of [
    ["zone", "zone"],
    ["ward", "ward"],
    ["block", "block"],
  ] as const) {
    const n = scopes.filter((g) => g.level === level).length;
    if (n) parts.push(`${n} ${noun}${n === 1 ? "" : "s"}`);
  }

  return (
    <p className="truncate text-2xs text-ink-muted" title={parts.join(" · ")}>
      {parts.join(" · ")}
    </p>
  );
}

/** Phone, session and lock status for an account row. */
function SecurityBadges({ user }: { user: AuthUser }) {
  const { t, lang } = useLanguage();
  const s = t.accountSecurity;
  const sec = user.security;
  if (!sec) return null;
  const chip = "rounded border px-1.5 py-0.5 text-2xs font-medium";
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
      {sec.disabled && (
        <span className={cn(chip, "border-status-critical/40 bg-status-critical/10 text-status-text-critical")}>
          {s.badgeDisabled}
        </span>
      )}
      {sec.device_pending && (
        <span className={cn(chip, "border-amber-300 bg-amber-50 text-amber-800")}>{s.badgePending}</span>
      )}
      {sec.online ? (
        <span className={cn(chip, "border-accent bg-accent-subtle text-accent-deep")}>{s.badgeOnline}</span>
      ) : sec.session_active ? (
        <span className={cn(chip, "border-line bg-surface-sunken text-ink-secondary")}>{s.badgeSignedIn}</span>
      ) : null}
      {!sec.online && (
        <span className="text-2xs text-ink-muted">
          {sec.last_seen_at
            ? s.badgeLastSeen(
                formatDateTime(sec.last_seen_at, lang, {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              )
            : s.badgeNeverSignedIn}
        </span>
      )}
      {user.role === "surveyor" &&
        (sec.device_bound ? (
          <span className={cn(chip, "border-line bg-surface-sunken text-ink-secondary")}>
            {sec.device_model ?? s.deviceHeading}
          </span>
        ) : (
          <span className={cn(chip, "border-line text-ink-muted")}>{s.badgeNoPhone}</span>
        ))}
      {sec.locked_minutes > 0 && (
        <span className={cn(chip, "border-status-critical/40 bg-status-critical/10 text-status-text-critical")}>
          {s.badgeLocked(sec.locked_minutes)}
        </span>
      )}
      {sec.late_uploads > 0 && (
        <span className={cn(chip, "border-amber-300 bg-amber-50 text-amber-800")}>
          {s.badgeLateUploads(sec.late_uploads)}
        </span>
      )}
    </div>
  );
}

function PasswordReset({
  disabled,
  onCancel,
  onSubmit,
}: {
  disabled: boolean;
  onCancel: () => void;
  onSubmit: (password: string) => void;
}) {
  const [value, setValue] = useState("");
  const tooShort = value.length > 0 && value.length < 8;
  const { t } = useLanguage();

  return (
    <div className="mt-2 rounded bg-surface-sunken p-2.5">
      {/* Smaller than the create-form field -- this sits inside a row, so it
          keeps its own sizing rather than the component default. */}
      <PasswordInput
        value={value}
        onChange={setValue}
        autoComplete="new-password"
        placeholder={t.usersPage.newPasswordPlaceholder}
        className="h-8 w-full rounded border-0 bg-surface-raised pl-2 pr-8 text-xs text-ink shadow-ring"
      />
      {tooShort && (
        <p className="mt-1 text-2xs text-status-text-critical">
          {t.usersPage.weakPassword}
        </p>
      )}
      <div className="mt-2 flex gap-1.5">
        <Button
          variant="primary"
          className="h-7 px-2 text-2xs"
          disabled={disabled || value.length < 8}
          onClick={() => onSubmit(value)}
        >
          {t.usersPage.setPassword}
        </Button>
        <Button className="h-7 px-2 text-2xs" onClick={onCancel}>
          {t.common.cancel}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-2xs text-ink-muted">{hint}</span>}
    </label>
  );
}
